import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Percent, DollarSign, AlertCircle, Save, Package, Globe, Building2, Eye, EyeOff, Loader2, Edit, Layers, Download, Upload, ChevronDown, ChevronRight } from 'lucide-react';

import { API_BASE } from '../lib/apiBase';
import ProductFamilyTabs, { getProductFamilyMeta } from '../components/ProductFamilyTabs';
import { FamilyBadge } from '../components/FamilyScopePicker';
import { Header } from '../components/Header';
const API_URL = API_BASE;

// Display helper — shows up to 4 decimals for customer-specific prices
// (matches the storage precision) but trims trailing zeros beyond the
// standard 2-decimal position so whole/half euros still read cleanly.
// "10.0000" → "10.00"; "9.5000" → "9.50"; "0.0803" → "0.0803".
const fmtPrice = (v) => {
  const num = Number(v);
  if (!Number.isFinite(num)) return '—';
  const s = num.toFixed(4);
  return s.replace(/(\.\d\d)0+$/, '$1');
};

export default function CustomerPricingPage() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [settings, setSettings] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [showAddOverride, setShowAddOverride] = useState(false);
  // Non-null while editing an existing override — reuses the AddOverrideModal
  // in edit mode so the create/edit UX stays in one place.
  const [editingOverride, setEditingOverride] = useState(null);
  // Bulk import/export controls
  const importFileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Collapse the (potentially very long) overrides table by default when
  // there are more than a handful. Admin can always click to re-open.
  const [overridesCollapsed, setOverridesCollapsed] = useState(false);
  useEffect(() => {
    // Auto-collapse when the list grows past 5 entries. Only run on the
    // first fetch (or when the count crosses the threshold) so a manual
    // expand isn't undone every render.
    if (overrides.length > 5 && !overridesCollapsed) {
      setOverridesCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrides.length]);
  
  // Bundle visibility state
  const [bundleVisibility, setBundleVisibility] = useState(null);
  const [bundleCategories, setBundleCategories] = useState({ global: [], company_assigned: [], other_available: [] });
  const [bundleLoading, setBundleLoading] = useState(false);
  // Phase 2: bundle family tab — drives /bundle-visibility?product_family=
  const [bundleFamily, setBundleFamily] = useState('vinyl');
  const [bundleFamilyCounts, setBundleFamilyCounts] = useState({ vinyl: 0, cd: 0, dvd: 0, bluray: 0 });

  const fetchBundleVisibility = useCallback(async (family) => {
    try {
      setBundleLoading(true);
      const token = localStorage.getItem('token');
      const fam = family || bundleFamily;
      const bundleRes = await fetch(
        `${API_URL}/api/product-config/admin/companies/${organizationId}/bundle-visibility?product_family=${encodeURIComponent(fam)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (bundleRes.ok) {
        const bundleData = await bundleRes.json();
        setBundleVisibility(bundleData.settings);
        setBundleCategories(bundleData.bundles);
      }
    } catch (err) {
      console.error('Failed to fetch bundle visibility:', err);
    } finally {
      setBundleLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, bundleFamily]);

  // Per-family counts for the tab badges
  const fetchBundleFamilyCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const families = ['vinyl', 'cd', 'dvd', 'bluray'];
      const results = await Promise.all(
        families.map(fam =>
          fetch(`${API_URL}/api/product-config/admin/companies/${organizationId}/bundle-visibility?product_family=${fam}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
              if (!d) return [fam, 0];
              const total = (d.bundles.global?.length || 0) + (d.bundles.company_assigned?.length || 0) + (d.bundles.other_available?.length || 0);
              return [fam, total];
            })
            .catch(() => [fam, 0])
        )
      );
      setBundleFamilyCounts(Object.fromEntries(results));
    } catch (err) {
      console.error('Failed to fetch family counts:', err);
    }
  }, [organizationId]);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch organization
      const orgRes = await fetch(`${API_URL}/api/organizations/${organizationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (orgRes.ok) {
        const orgData = await orgRes.json();
        setOrganization(orgData);
      }

      // Fetch settings
      const settingsRes = await fetch(`${API_URL}/api/pricing/customers/${organizationId}/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
        setGlobalDiscount(settingsData.global_discount_percent || 0);
        setNotes(settingsData.notes || '');
      }

      // Fetch overrides
      const overridesRes = await fetch(`${API_URL}/api/pricing/customers/${organizationId}/overrides`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (overridesRes.ok) {
        const overridesData = await overridesRes.json();
        setOverrides(overridesData);
      }

      // Fetch all SALES rate items across active rate cards for the
      // override dropdown. These are customer-facing SKUs that override
      // our sales prices — not cost/BOM items. We pass `product_family=all`
      // so admins can pick items from any active rate card regardless of
      // the currently active family filter on the page.
      const itemsRes = await fetch(`${API_URL}/api/sales/rate-items/cross-card?product_family=all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (itemsRes.ok) {
        const itemsData = await itemsRes.json();
        // Map to a uniform shape so the dropdown rendering doesn't have to
        // know whether the source is a Sales Rate item or legacy Price item.
        setPriceItems(
          (Array.isArray(itemsData) ? itemsData : []).map((it) => ({
            id: it.id,
            sales_rate_item_id: it.id,
            sku: it.sales_sku,
            name: it.display_name,
            category: it.category || it.source_rate_card_name,
            source_rate_card_name: it.source_rate_card_name,
            source_rate_card_product_family: it.source_rate_card_product_family,
          }))
        );
      }
      
      // Bundle visibility is loaded separately (family-aware). See useEffect below.
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refetch the family-filtered bundle visibility whenever the active family
  // tab changes (also runs on initial mount).
  useEffect(() => {
    if (organizationId) {
      fetchBundleVisibility(bundleFamily);
    }
  }, [organizationId, bundleFamily, fetchBundleVisibility]);

  // Family counts run once per org load
  useEffect(() => {
    if (organizationId) {
      fetchBundleFamilyCounts();
    }
  }, [organizationId, fetchBundleFamilyCounts]);
  
  // Toggle bundle visibility for this company
  const handleToggleBundle = async (bundleId, action) => {
    setBundleLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/product-config/admin/companies/${organizationId}/bundle-visibility/toggle?bundle_id=${bundleId}&action=${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Refresh data with the active family filter
      await fetchBundleVisibility(bundleFamily);
      fetchBundleFamilyCounts();
    } catch (err) {
      console.error('Failed to toggle bundle:', err);
    } finally {
      setBundleLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/pricing/customers/${organizationId}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          global_discount_percent: parseFloat(globalDiscount) || 0,
          notes: notes || null
        })
      });
      fetchData();
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOverride = async (overrideId) => {
    if (!window.confirm('Delete this price override?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/pricing/customers/${organizationId}/overrides/${overrideId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Failed to delete override:', err);
    }
  };

  // Export the current customer's overrides as a CSV. Round-trips
  // back through the /import endpoint so bulk edits in Excel just work.
  const handleExportOverrides = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/pricing/customers/${organizationId}/overrides/export`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `overrides_${organization?.name || 'customer'}.csv`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed: ' + (err.message || err));
    }
  };

  const handleImportOverrides = async (file, replaceExisting) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(
        `${API_URL}/api/pricing/customers/${organizationId}/overrides/import?replace_existing=${replaceExisting ? 'true' : 'false'}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setImportResult(data);
      await fetchData();
    } catch (err) {
      console.error('Import failed:', err);
      alert('Import failed: ' + (err.message || err));
    } finally {
      setImporting(false);
      // Reset file input so the same file can be re-imported after edits.
      if (importFileInputRef.current) importFileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-pricing-page">
      <Header />
      {/* Page title — old back button lived here; global Admin Header
          now handles cross-section navigation. */}
      <div className="bg-white border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight text-gray-900">Customer Pricing</h1>
              <p className="text-sm text-gray-500">{organization?.name || 'Loading...'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Global Settings */}
        <div className="bg-white rounded-lg border border-border p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Global Settings</h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Global Discount %
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={globalDiscount}
                  onChange={(e) => setGlobalDiscount(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <Percent className="w-5 h-5 text-gray-400" />
              </div>
              <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                Applied to all customer-facing <strong>sales prices</strong>: Guided Setup,
                Expert Setup, sales price-list items, customer-specific sales prices,
                sales documents and invoices.
                <br />
                <span className="text-gray-400">
                  Does NOT apply to Quick Bundles (campaign pricing), fixed-price overrides,
                  or supplier / BOM cost.
                </span>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Internal notes about this customer's pricing"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Item-Specific Overrides */}
        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <button
              onClick={() => setOverridesCollapsed(v => !v)}
              className="flex items-center gap-2 text-left group"
              data-testid="overrides-toggle-collapse"
            >
              {overridesCollapsed ? (
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-700" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-gray-700" />
              )}
              <div>
                <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  Item-Specific Overrides
                  {overrides.length > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {overrides.length}
                    </span>
                  )}
                </h2>
                <p className="text-sm text-gray-500">Set fixed prices or extra discounts per item</p>
              </div>
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleExportOverrides}
                disabled={overrides.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                title={overrides.length === 0 ? 'No overrides to export' : 'Download all overrides as CSV'}
                data-testid="overrides-export-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <input
                ref={importFileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="overrides-import-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const replace = window.confirm(
                    `Import ${f.name}.\n\n` +
                    'Click OK to REPLACE all current overrides for this customer with the CSV rows.\n\n' +
                    'Click Cancel to MERGE — imported rows upsert onto existing overrides, un-mentioned SKUs are kept as-is.'
                  );
                  handleImportOverrides(f, replace);
                }}
              />
              <button
                onClick={() => importFileInputRef.current?.click()}
                disabled={importing}
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-700 disabled:opacity-40"
                data-testid="overrides-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {importing ? 'Importing…' : 'Import CSV'}
              </button>
              <button
                onClick={() => setShowAddOverride(true)}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                data-testid="overrides-add-btn"
              >
                <Plus className="w-4 h-4" />
                Add Override
              </button>
            </div>
          </div>

          {/* Import result banner — visible until the next fetch or manual dismiss */}
          {importResult && (
            <div
              className={`mb-3 p-3 rounded-lg text-sm border ${
                importResult.errors?.length
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-800'
              }`}
              data-testid="overrides-import-result"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">
                    Imported: {importResult.created} created · {importResult.updated} updated
                    {importResult.skipped > 0 ? ` · ${importResult.skipped} skipped` : ''}
                  </div>
                  {importResult.errors?.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer">
                        {importResult.errors.length} error{importResult.errors.length === 1 ? '' : 's'} — click to view
                      </summary>
                      <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                        {importResult.errors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
                <button
                  onClick={() => setImportResult(null)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {overridesCollapsed && overrides.length > 0 ? (
            <button
              onClick={() => setOverridesCollapsed(false)}
              className="w-full text-left text-sm text-indigo-600 hover:text-indigo-700 py-2"
              data-testid="overrides-show-all"
            >
              Show {overrides.length} override{overrides.length === 1 ? '' : 's'} →
            </button>
          ) : overrides.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No item-specific overrides yet
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fixed Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Extra Discount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Volume Tiers</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {overrides.map((override) => (
                    <tr key={override.id} className="hover:bg-gray-50" data-testid={`override-row-${override.id}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{override.price_item_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{override.price_item_sku}</td>
                      <td className="px-4 py-3 text-right">
                        {override.tiers && override.tiers.length > 0 ? (
                          <span className="text-gray-400 text-xs italic">(tiered)</span>
                        ) : override.fixed_price ? (
                          <span className="font-medium text-green-600">€{fmtPrice(override.fixed_price)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {override.tiers && override.tiers.length > 0 ? (
                          <span className="text-gray-400 text-xs italic">(tiered)</span>
                        ) : override.discount_percent ? (
                          <span className="font-medium text-blue-600">-{override.discount_percent}%</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {override.tiers && override.tiers.length > 0 ? (
                          <div className="flex flex-wrap gap-1" data-testid={`override-tiers-${override.id}`}>
                            {override.tiers.map((t) => (
                              <span
                                key={t.id || t.min_qty}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100"
                              >
                                <Layers className="w-3 h-3" />
                                {t.min_qty}+ {t.unit_price != null ? `€${fmtPrice(t.unit_price)}` : `-${t.discount_percent}%`}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Flat</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setEditingOverride(override)}
                          className="p-1 text-gray-400 hover:text-indigo-600 transition-colors mr-1"
                          title="Edit override"
                          data-testid={`edit-override-${override.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOverride(override.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete override"
                          data-testid={`delete-override-${override.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pricing Logic Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">How customer-facing sales prices are calculated</h4>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Start with the <strong>Sales Rate Card tier price</strong> for the chosen quantity (Guided / Expert Setup &amp; sales documents)</li>
            <li>If a <strong>Volume-tier override</strong> is set, the pricing engine picks the override tier with the largest min-qty ≤ ordered quantity and uses its Fixed € (or % off list at that qty)</li>
            <li>Otherwise, if a <strong>Fixed Sales Price</strong> is set → use that verbatim for every quantity</li>
            <li>Otherwise, apply the <strong>Item Discount %</strong> (if set) to every tier</li>
            <li>Then apply the customer&apos;s <strong>Global Discount %</strong></li>
            <li>Result = <strong>Final Sales Price</strong> shown on the quote / invoice</li>
          </ol>
          <p className="mt-2 text-xs text-blue-600/80">
            Quick Bundles, supplier purchase prices and BOM cost are <strong>not</strong> affected.
          </p>
        </div>
        
        {/* Quick Order Bundles Visibility */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold">Quick Order Bundles</h3>
            {bundleLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Control which product bundles this customer sees in Quick Order mode. Global bundles are shown to all customers by default.
          </p>

          {/* Phase 2: family tabs — filter visibility lists per product family */}
          <div className="mb-4 flex items-center justify-between">
            <ProductFamilyTabs
              value={bundleFamily}
              onChange={setBundleFamily}
              counts={bundleFamilyCounts}
            />
            <span className="text-xs text-slate-500">
              Showing <span className="font-medium text-slate-700">{getProductFamilyMeta(bundleFamily).label}</span> bundles
            </span>
          </div>
          
          {/* Global Bundles - Always Default */}
          {bundleCategories.global.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-indigo-500" />
                <h4 className="text-sm font-semibold text-slate-800">Global Bundles</h4>
                <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">Default for all</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Global bundles appear for all customers. Hide specific bundles to exclude them from this customer only.
              </p>
              <div className="space-y-2">
                {bundleCategories.global.map(bundle => (
                  <div 
                    key={bundle.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      bundle.is_hidden 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-emerald-50 border-emerald-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {bundle.is_hidden ? (
                        <div className="p-1.5 bg-red-100 rounded-full">
                          <EyeOff className="w-4 h-4 text-red-500" />
                        </div>
                      ) : (
                        <div className="p-1.5 bg-emerald-100 rounded-full">
                          <Eye className="w-4 h-4 text-emerald-600" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`font-medium text-sm ${bundle.is_hidden ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                            {bundle.title}
                          </p>
                          <FamilyBadge family={bundle.product_family} />
                          {bundle.is_hidden && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full font-medium">
                              Hidden for this customer
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {bundle.tier} • SKU: {bundle.base_sales_sku}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleBundle(bundle.id, bundle.is_hidden ? 'show' : 'hide')}
                      disabled={bundleLoading}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        bundle.is_hidden 
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      {bundle.is_hidden ? 'Unhide' : 'Hide'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Enabled for This Customer - Bundles explicitly enabled */}
          {bundleCategories.other_available.filter(b => b.is_enabled).length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-emerald-500" />
                <h4 className="text-sm font-semibold text-slate-800">Enabled for This Customer</h4>
                <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full">Custom</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                These non-global bundles have been specifically enabled for this customer.
              </p>
              <div className="space-y-2">
                {bundleCategories.other_available.filter(b => b.is_enabled).map(bundle => (
                  <div 
                    key={bundle.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-emerald-50 border-emerald-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-emerald-100 rounded-full">
                        <Eye className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-slate-900">{bundle.title}</p>
                          <FamilyBadge family={bundle.product_family} />
                          <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                            Enabled
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">
                          {bundle.tier} • SKU: {bundle.base_sales_sku}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleBundle(bundle.id, 'disable')}
                      disabled={bundleLoading}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                    >
                      Disable
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Company-Assigned Bundles */}
          {bundleCategories.company_assigned.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-amber-500" />
                <h4 className="text-sm font-semibold text-slate-800">Assigned to This Customer</h4>
                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">Assigned</span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                These bundles are directly assigned to this customer in the bundle configuration.
              </p>
              <div className="space-y-2">
                {bundleCategories.company_assigned.map(bundle => (
                  <div 
                    key={bundle.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 border-amber-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-amber-100 rounded-full">
                        <Building2 className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-slate-900">{bundle.title}</p>
                          <FamilyBadge family={bundle.product_family} />
                        </div>
                        <p className="text-xs text-slate-500">
                          {bundle.tier} • SKU: {bundle.base_sales_sku}
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                      Always visible
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Other Available Bundles - Only show non-enabled ones */}
          {bundleCategories.other_available.filter(b => !b.is_enabled).length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-slate-400" />
                <h4 className="text-sm font-semibold text-slate-800">Other Available Bundles</h4>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                These bundles are not global. Enable them to make them visible to this customer.
              </p>
              <div className="space-y-2">
                {bundleCategories.other_available.filter(b => !b.is_enabled).map(bundle => (
                  <div 
                    key={bundle.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 border-slate-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-slate-200 rounded-full">
                        <EyeOff className="w-4 h-4 text-slate-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-slate-500">{bundle.title}</p>
                          <FamilyBadge family={bundle.product_family} />
                          <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-500 rounded-full">
                            Not visible
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">
                          {bundle.tier} • SKU: {bundle.base_sales_sku}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggleBundle(bundle.id, 'enable')}
                      disabled={bundleLoading}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                    >
                      Enable
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {bundleCategories.global.length === 0 && 
           bundleCategories.company_assigned.length === 0 && 
           bundleCategories.other_available.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p>No {getProductFamilyMeta(bundleFamily).label} Quick Order bundles configured yet.</p>
              <p className="text-xs text-slate-400 mt-1">Switch family tabs above to see other bundles.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Override Modal */}
      {showAddOverride && (
        <AddOverrideModal
          organizationId={organizationId}
          priceItems={priceItems}
          existingOverrides={overrides}
          onClose={() => setShowAddOverride(false)}
          onSave={() => {
            setShowAddOverride(false);
            fetchData();
          }}
        />
      )}

      {/* Edit Override Modal — reuses AddOverrideModal in edit mode */}
      {editingOverride && (
        <AddOverrideModal
          organizationId={organizationId}
          priceItems={priceItems}
          existingOverrides={overrides}
          editingOverride={editingOverride}
          onClose={() => setEditingOverride(null)}
          onSave={() => {
            setEditingOverride(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

function AddOverrideModal({ organizationId, priceItems, existingOverrides, editingOverride = null, onClose, onSave }) {
  const isEdit = !!editingOverride;
  // In edit mode the item is locked to whatever the override was created
  // for (backend PATCH doesn't accept a different item id anyway). In
  // create mode, filter out items that already have an override so we
  // don't show duplicates.
  const initialItemId =
    editingOverride?.sales_rate_item_id ||
    editingOverride?.price_item_id ||
    '';
  const [selectedItem, setSelectedItem] = useState(initialItemId);
  const [fixedPrice, setFixedPrice] = useState(
    editingOverride?.fixed_price != null ? String(editingOverride.fixed_price) : ''
  );
  const [discountPercent, setDiscountPercent] = useState(
    editingOverride?.discount_percent != null ? String(editingOverride.discount_percent) : ''
  );
  const initialTiers = editingOverride?.tiers && editingOverride.tiers.length > 0
    ? editingOverride.tiers.map((t) => ({
        min_qty: String(t.min_qty ?? ''),
        unit_price: t.unit_price != null ? String(t.unit_price) : '',
        discount_percent: t.discount_percent != null ? String(t.discount_percent) : '',
      }))
    : [];
  const [tierMode, setTierMode] = useState(initialTiers.length > 0);
  const [tiers, setTiers] = useState(
    initialTiers.length > 0
      ? initialTiers
      : [{ min_qty: '', unit_price: '', discount_percent: '' }]
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const existingItemIds = new Set([
    ...existingOverrides.map((o) => o.sales_rate_item_id).filter(Boolean),
    ...existingOverrides.map((o) => o.price_item_id).filter(Boolean),
  ]);
  const availableItems = isEdit
    ? priceItems.filter((it) => it.id === initialItemId)
    : priceItems.filter((it) => !existingItemIds.has(it.id));

  const addTier = () => setTiers((prev) => [...prev, { min_qty: '', unit_price: '', discount_percent: '' }]);
  const removeTier = (idx) => setTiers((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const updateTier = (idx, field, value) =>
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));

  const validateTiers = () => {
    // Client-side mirror of the backend validator so admins see mistakes
    // inline before submit. Returns null on success, error string on fail.
    const seen = new Set();
    for (let i = 0; i < tiers.length; i += 1) {
      const t = tiers[i];
      const min = parseInt(t.min_qty, 10);
      if (!Number.isFinite(min) || min < 1) return `Tier ${i + 1}: min_qty must be a positive whole number`;
      if (seen.has(min)) return `Tier ${i + 1}: min_qty ${min} is duplicated — each breakpoint must be unique`;
      seen.add(min);
      const hasPrice = t.unit_price !== '' && t.unit_price != null;
      const hasDisc = t.discount_percent !== '' && t.discount_percent != null;
      if (hasPrice === hasDisc) return `Tier ${min}+: set exactly ONE of Fixed € or Discount %`;
      if (hasPrice && Number(t.unit_price) < 0) return `Tier ${min}+: price cannot be negative`;
      if (hasDisc) {
        const pct = Number(t.discount_percent);
        if (!(pct > 0 && pct <= 100)) return `Tier ${min}+: discount % must be between 0 and 100`;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    let payloadTiers = null;
    if (tierMode) {
      const err = validateTiers();
      if (err) {
        setError(err);
        return;
      }
      payloadTiers = tiers.map((t) => ({
        min_qty: parseInt(t.min_qty, 10),
        unit_price: t.unit_price !== '' ? parseFloat(t.unit_price) : null,
        discount_percent: t.discount_percent !== '' ? parseFloat(t.discount_percent) : null,
      }));
    } else if (!fixedPrice && !discountPercent) {
      setError('Either fixed price, discount percent, or volume tiers must be set');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = isEdit
        ? `${API_URL}/api/pricing/customers/${organizationId}/overrides/${editingOverride.id}`
        : `${API_URL}/api/pricing/customers/${organizationId}/overrides`;
      const method = isEdit ? 'PATCH' : 'POST';

      // Create payload includes the item link; edit payload only mutates
      // the pricing shape (item link is immutable on the backend).
      let body;
      if (isEdit) {
        body = tierMode
          ? { tiers: payloadTiers, fixed_price: null, discount_percent: null }
          : {
              tiers: [], // explicit clear so any pre-existing tiers drop off
              fixed_price: fixedPrice ? parseFloat(fixedPrice) : 0,
              discount_percent: discountPercent ? parseFloat(discountPercent) : 0,
            };
      } else {
        body = {
          organization_id: organizationId,
          sales_rate_item_id: selectedItem,
          fixed_price: !tierMode && fixedPrice ? parseFloat(fixedPrice) : null,
          discount_percent: !tierMode && discountPercent ? parseFloat(discountPercent) : null,
          tiers: tierMode ? payloadTiers : null,
        };
      }

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || 'Failed to save override');
      }
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? 'Edit Price Override' : 'Add Price Override'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sales Price Item *</label>
            <select
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
              required
              disabled={isEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
              data-testid="override-item-select"
            >
              <option value="">Select a sales SKU…</option>
              {availableItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.sku} — {item.name}
                  {item.source_rate_card_name ? `  ·  ${item.source_rate_card_name}` : ''}
                </option>
              ))}
              {/* When editing, ensure the current item stays selectable even
                  if it's not in the filtered availableItems list (e.g. a
                  legacy price_item_id-based override). */}
              {isEdit && !availableItems.find((it) => it.id === initialItemId) && (
                <option value={initialItemId}>
                  {editingOverride?.price_item_sku} — {editingOverride?.price_item_name}
                </option>
              )}
            </select>
            {!isEdit && availableItems.length === 0 && (
              <p className="mt-1 text-sm text-amber-600">All sales items already have overrides</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {isEdit
                ? 'Item is locked once an override is created. Delete and re-add to change it.'
                : 'Pulls customer-facing SKUs from active Sales Rate Cards. Supplier / BOM items are not listed.'}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={tierMode}
                onChange={(e) => setTierMode(e.target.checked)}
                className="w-4 h-4"
                data-testid="override-tier-mode-toggle"
              />
              <Layers className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-gray-800">Volume-tier pricing</span>
              <span className="text-xs text-gray-500">
                — customer&apos;s price depends on ordered quantity (matches Sales Rate Card tiers)
              </span>
            </label>
          </div>

          {!tierMode ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Sales Price (€)</label>
                <input
                  type="number"
                  value={fixedPrice}
                  onChange={(e) => setFixedPrice(e.target.value)}
                  step="0.0001"
                  min="0"
                  placeholder="Leave empty to use calculated price"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="override-fixed-price"
                />
                <p className="mt-1 text-xs text-gray-500">Overrides all calculations if set — applies to every quantity. Up to 4 decimals supported (e.g. 0.0803).</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Extra Discount %</label>
                <input
                  type="number"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(e.target.value)}
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="Additional item-specific discount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="override-discount-percent"
                />
                <p className="mt-1 text-xs text-gray-500">Applied before global customer discount — every quantity gets the same %.</p>
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="override-tier-editor">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800">Volume tiers</p>
                  <p className="text-xs text-gray-500">
                    Per row set EITHER a fixed € price OR a % discount. Pricing engine picks the tier with the largest min_qty ≤ ordered quantity.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addTier}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  data-testid="override-tier-add"
                >
                  <Plus className="w-3 h-3" /> Add Tier
                </button>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Min Qty</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fixed € / unit</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">OR Discount %</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tiers.map((t, idx) => (
                      <tr key={idx} data-testid={`override-tier-row-${idx}`}>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={t.min_qty}
                            onChange={(e) => updateTier(idx, 'min_qty', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded"
                            placeholder="100"
                            data-testid={`override-tier-min-qty-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.0001"
                            value={t.unit_price}
                            onChange={(e) => {
                              updateTier(idx, 'unit_price', e.target.value);
                              if (e.target.value) updateTier(idx, 'discount_percent', '');
                            }}
                            className="w-28 px-2 py-1 border border-gray-300 rounded"
                            placeholder="10.0000"
                            data-testid={`override-tier-price-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={t.discount_percent}
                            onChange={(e) => {
                              updateTier(idx, 'discount_percent', e.target.value);
                              if (e.target.value) updateTier(idx, 'unit_price', '');
                            }}
                            className="w-28 px-2 py-1 border border-gray-300 rounded"
                            placeholder="15"
                            data-testid={`override-tier-discount-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeTier(idx)}
                            disabled={tiers.length === 1}
                            className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                            title={tiers.length === 1 ? 'At least one tier is required' : 'Remove tier'}
                            data-testid={`override-tier-remove-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500">
                Example: <span className="font-mono">100+ €10.00, 200+ €9.50, 500+ €8.75</span> — an order of 250 units pays €9.50, an order of 700 pays €8.75. Prices support up to 4 decimals (e.g. <span className="font-mono">€0.0803</span> for low-value items).
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              data-testid="override-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (!isEdit && !selectedItem)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              data-testid="override-save-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Override'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
